/**
 * How an artifact is SAVED — one rule, shared by every surface with a Download
 * control (Pieces rail, studio canvas, files browser, creations grid).
 *
 * Why this is a module and not three call sites: an artifact's storage object is
 * keyed by its `public_id`, so the url's last path segment is a uuid
 * (`…/0b4fbdf3-7062-44dc-a4b4-312e0343856d.docx`). The HTML `download`
 * attribute, which would carry the real name, is IGNORED by every browser when
 * the href is cross-origin — and a re-signed Supabase url always is. The
 * attribute fails silently, the browser falls back to the url's basename, and
 * the user's "Declaration Landmark - FINAL.docx" lands in ~/Downloads as a uuid
 * (prod 09-16, reported for both pdf and docx).
 *
 * The name therefore has to ride the URL itself: Supabase Storage honours
 * `?download=<name>` on a signed url by emitting
 * `Content-Disposition: attachment; filename=<name>`, which no origin rule can
 * strip. A `/v/<id>` door does its own disposition AND names the file itself, and
 * honours only `?download=1`: a `download=<name>` on a door opened it inline and
 * saved it under the storage basename. Every creation's url is its door now.
 *
 * This module imports no components on purpose — `files-browser` already
 * imports `studio-canvas`, so a rule living in either would close an import
 * cycle, and a cycle here reads as an `undefined` constant at load with no error.
 */
import { briefTitle } from "@/lib/creations/creationTitle";

/** The fields the rule reads. Structural, so a `MediaArtifact`, a creations row
 *  and a preview target all satisfy it without a conversion step. */
export interface DownloadableArtifact {
  /** `public_id` — the /v proxy's id, used only when there is no url. */
  id?: string | null;
  url?: string | null;
  filename?: string | null;
  caption?: string | null;
  brief?: string | null;
  /** Where the url has no basename to read (a /v door), the extension comes from here. */
  mime_type?: string | null;
}

/** The extension a stored object of this type has, for the formats we store. */
const EXT_FROM_MIME: Record<string, string> = {
  "image/png": "png",
  "image/jpeg": "jpg",
  "image/webp": "webp",
  "image/gif": "gif",
  "image/svg+xml": "svg",
  "video/mp4": "mp4",
  "video/webm": "webm",
  "video/quicktime": "mov",
  "audio/mpeg": "mp3",
  "audio/mp3": "mp3",
  "audio/wav": "wav",
  "audio/x-wav": "wav",
  "audio/webm": "webm",
  "audio/ogg": "ogg",
  "application/pdf": "pdf",
  "text/html": "html",
  "text/markdown": "md",
  "text/csv": "csv",
  "text/plain": "txt",
  "application/json": "json",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
    "docx",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "xlsx",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation":
    "pptx",
};

/** The extension for a mime type, '' when it is unknown or absent. */
export function extFromMime(mime: string | null | undefined): string {
  return (mime && EXT_FROM_MIME[mime.split(";")[0].trim().toLowerCase()]) || "";
}

const DOOR_PATH =
  /^\/v\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})\/?$/i;

/**
 * The id of a `/v/<id>` door, relative or absolute (the stored column is written
 * on WEB_APP_URL, which may be the other of apex/www), or null for anything else.
 * Host-agnostic like `fileViewerUrl`: the id is only ever sent back to OUR door,
 * relative, where the gate decides.
 */
export function doorIdOf(url: string | null | undefined): string | null {
  if (!url) return null;
  try {
    return (
      DOOR_PATH.exec(new URL(url, "http://door.invalid").pathname)?.[1] ?? null
    );
  } catch {
    return null;
  }
}

function extOf(name: string): string {
  const dot = name.lastIndexOf(".");
  return dot === -1 ? "" : name.slice(dot + 1).toLowerCase();
}

/** The basename inside a storage url, or null when the url is not that shape. */
export function nameFromUrl(url: string | null | undefined): string | null {
  if (!url) return null;
  try {
    const path = url.split("?")[0];
    const base = decodeURIComponent(path.split("/").pop() ?? "");
    return base && base.includes(".") ? base : null;
  } catch {
    return null;
  }
}

/**
 * The name to save under, or null when nothing names this artifact.
 *
 * Null rather than a last-resort basename: the basename IS the uuid this module
 * exists to avoid, and asking storage to save "0b4fbdf3-….docx" under the name
 * "0b4fbdf3-….docx" would dress the bug up as a fix. A null lets the caller
 * serve the bare url — the same imperfect name, but no pretence.
 *
 * Presented files carry `filename` (386/386 of them on prod, so this is the
 * live path). Studio media carries neither filename nor caption and is named
 * from its brief, which is prose with no extension — so the extension the
 * storage object actually has is borrowed. `bin` is excluded: it is the absence
 * of a type, and stamping it would save "….bin" for every legacy object.
 */
export function artifactDownloadName(a: DownloadableArtifact): string | null {
  if (a.filename) return a.filename;
  const named = a.caption || briefTitle(a.brief) || null;
  if (!named) return null;
  if (extOf(named)) return named;
  const stored = nameFromUrl(a.url);
  const ext = stored ? extOf(stored) : extFromMime(a.mime_type);
  return ext && ext !== "bin" ? `${named}.${ext}` : named;
}

/**
 * Ask Supabase Storage to serve this url as an attachment under `name`.
 * Appended, never substituted: a signed url's `token` is part of the signature,
 * and dropping it turns the download into a 400.
 */
export function withDownloadName(url: string, name: string): string {
  const sep = url.includes("?") ? "&" : "?";
  return `${url}${sep}download=${encodeURIComponent(name)}`;
}

/**
 * The href a Download control should point at. Null only when the artifact can
 * be served neither by url nor by proxy id.
 */
export function artifactDownloadHref(a: DownloadableArtifact): string | null {
  // A door names its own download; relative, so the session cookie rides along.
  const door = doorIdOf(a.url);
  if (door) return `/v/${door}?download=1`;
  const name = artifactDownloadName(a);
  if (a.url) return name ? withDownloadName(a.url, name) : a.url;
  return a.id ? `/v/${a.id}?download=1` : null;
}
